// Master milestone data types and interfaces

// Import AdminNode PAS types for developmental age scoring
import { 
  AdminNodeParamsDto, 
  AdminNodeMeanDto, 
  AdminNodeProbDto, 
  AdminNodeCutsDto 
} from './adminSegment';

// Master milestone definition from datacenter
export interface MasterMilestone {
  mvar: string;             // Unique milestone identifier (simplified from mvarid)
  domain: string;           // Domain key: 'communication', 'motor', 'fineMotor', etc.
  itemage: number;          // Age in months when milestone typically achieved
  seqnum: number;           // Sequence number within domain for ordering (simplified from domainseqnum)
  lastUpdated: string;      // Server timestamp for sync management
  version: number;          // Version number for conflict resolution
  
  // PAS information for developmental age scoring (from AdminNodeDto)
  pasParams?: AdminNodeParamsDto[];   // Node parameters configuration
  pasMeans?: AdminNodeMeanDto[];      // Node means configuration  
  pasProbs?: AdminNodeProbDto[];      // Node probability configuration
  pasCuts?: AdminNodeCutsDto[];       // Node cuts configuration
}

// Milestone text content (multilingual)
export interface MilestoneText {
  mvar: string;             // Links to MasterMilestone.mvar (simplified from mvarid)
  languagetype: string;     // Language code: 'en', 'es', 'fr', etc.
  htmltext: string;         // Rich text content (HTML formatted)
  lastUpdated: string;      // Server timestamp for sync management
  version: number;          // Version number for conflict resolution
}

// Milestone answer states
export type MilestoneAnswerValue = 'yes' | 'sometimes' | 'not-yet';

// Milestone answer tracking for user responses
export interface MilestoneAns {
  ansid: string;            // Unique answer identifier
  mvar: string;             // Links to MasterMilestone.mvar
  value: MilestoneAnswerValue; // User response: yes, sometimes, not-yet
  seqnum: number;           // Sequence number for ordering answers
  lastUpdated: string;      // When this answer was last modified
  version: number;          // Version for conflict resolution
}

// Milestone answer text content (for custom answer explanations)
export interface MilestoneAnsText {
  ansid: string;            // Links to MilestoneAns.ansid
  languagetype: string;     // Language code: 'en', 'es', 'fr', etc.
  htmltext: string;         // Rich text explanation/notes (HTML formatted)
  lastUpdated: string;      // Server timestamp for sync management
  version: number;          // Version number for conflict resolution
}

// Concern answer states
export type ConcernAnswerValue = 'yes' | 'no';

// Concern answer tracking for user responses
export interface ConcernAns {
  ansid: string;            // Unique answer identifier
  mvar: string;             // Links to concern identifier (using same pattern as milestones)
  value: ConcernAnswerValue; // User response: yes, no
  seqnum: number;           // Sequence number for ordering answers
  lastUpdated: string;      // When this answer was last modified
  version: number;          // Version for conflict resolution
}

// Concern answer text content (for custom concern explanations)
export interface ConcernAnsText {
  ansid: string;            // Links to ConcernAns.ansid
  languagetype: string;     // Language code: 'en', 'es', 'fr', etc.
  htmltext: string;         // Rich text explanation/notes (HTML formatted)
  lastUpdated: string;      // Server timestamp for sync management
  version: number;          // Version number for conflict resolution
}

// Combined milestone with text for UI consumption
export interface MilestoneWithText extends MasterMilestone {
  text: string;             // Resolved text for current language
  isAvailable: boolean;     // Whether text is available in current language
}

// Language support configuration
export interface LanguageConfig {
  code: string;             // ISO language code
  name: string;             // Display name
  isDefault: boolean;       // Whether this is the default language
  isSupported: boolean;     // Whether this language is fully supported
}

// Master milestone storage structure
export interface MasterMilestoneData {
  milestones: MasterMilestone[];
  milestoneTexts: MilestoneText[];
  milestoneAnswers: MilestoneAns[];
  milestoneAnswerTexts: MilestoneAnsText[];
  concernAnswers: ConcernAns[];
  concernAnswerTexts: ConcernAnsText[];
  languages: LanguageConfig[];
  lastSyncTimestamp: string;
  dataVersion: string;      // Overall data version for bulk updates
}

// Indexed access structures for performance
export interface MilestoneIndexes {
  byMvar: Map<string, MasterMilestone>;
  byDomain: Map<string, MasterMilestone[]>;
  textByMvar: Map<string, Map<string, MilestoneText>>; // mvar -> language -> text
  sequenceByDomain: Map<string, MasterMilestone[]>; // domain -> sorted by seqnum
  answersByMvar: Map<string, MilestoneAns[]>; // mvar -> answers
  concernAnswersByMvar: Map<string, ConcernAns[]>; // mvar -> concern answers
}

// Milestone query and filtering
export interface MilestoneQuery {
  domain?: string;
  minAge?: number;
  maxAge?: number;
  language?: string;
  limit?: number;
  offset?: number;
}

// Milestone sync metadata
export interface MilestoneSyncMetadata {
  totalMilestones: number;
  totalTexts: number;
  totalAnswers: number;
  totalConcernAnswers: number;
  supportedLanguages: string[];
  lastFullSync: string;
  incrementalSyncAvailable: boolean;
  dataIntegrity: {
    milestonesWithoutText: string[];  // mvars missing text
    orphanedTexts: string[];          // texts without corresponding milestones
    duplicateSequences: Array<{       // domain sequence conflicts
      domain: string;
      duplicatedSeqNums: number[];
    }>;
    orphanedAnswers: string[];        // answers without corresponding milestones
  };
}

// Storage and retrieval interfaces
export interface MasterMilestoneStorage {
  // Data management
  loadMasterData(): Promise<MasterMilestoneData | null>;
  saveMasterData(data: MasterMilestoneData): Promise<void>;
  updateMilestones(milestones: MasterMilestone[]): Promise<void>;
  updateMilestoneTexts(texts: MilestoneText[]): Promise<void>;
  updateMilestoneAnswers(answers: MilestoneAns[]): Promise<void>;
  updateConcernAnswers(answers: ConcernAns[]): Promise<void>;
  
  // Query methods
  getMilestonesByDomain(domain: string, language?: string): Promise<MilestoneWithText[]>;
  getMilestoneByMvar(mvar: string, language?: string): Promise<MilestoneWithText | null>;
  getMilestonesSeqnum(domain: string, seqnum: number, language?: string): Promise<MilestoneWithText[]>;
  queryMilestones(query: MilestoneQuery): Promise<MilestoneWithText[]>;
  
  // Answer methods
  getMilestoneAnswers(mvar: string): Promise<MilestoneAns[]>;
  getConcernAnswers(mvar: string): Promise<ConcernAns[]>;
  
  // Index management
  buildIndexes(): Promise<MilestoneIndexes>;
  validateDataIntegrity(): Promise<MilestoneSyncMetadata>;
  
  // Sync support
  getDataVersion(): Promise<string>;
  getSyncMetadata(): Promise<MilestoneSyncMetadata>;
  
  // Cache management
  clearCache(): Promise<void>;
  preloadForDomain(domain: string): Promise<void>;
  
  // Development utilities
  loadMockData(): Promise<void>;
}

// Performance optimization constants
export const MILESTONE_STORAGE_CONFIG = {
  // Storage keys
  MASTER_DATA_KEY: 'masterMilestones',
  CACHE_KEY_PREFIX: 'milestone_cache_',
  INDEX_KEY: 'milestone_indexes',
  
  // Performance settings
  MAX_CACHE_SIZE: 50, // Cache up to 50 milestones in memory
  INDEX_REBUILD_INTERVAL: 24 * 60 * 60 * 1000, // 24 hours
  PRELOAD_DOMAINS: ['communication', 'motor'], // Always preload these
  
  // Language settings
  DEFAULT_LANGUAGE: 'en',
  FALLBACK_LANGUAGES: ['en'], // Fallback chain if requested language unavailable
  
  // Sync settings
  BATCH_SIZE: 25, // Process milestones in batches of 25
  MAX_RETRY_ATTEMPTS: 3,
  SYNC_TIMEOUT: 30000, // 30 seconds
};

// Utility type guards
export const isMasterMilestone = (obj: any): obj is MasterMilestone => {
  return obj && 
    typeof obj.mvar === 'string' &&
    typeof obj.domain === 'string' &&
    typeof obj.itemage === 'number' &&
    typeof obj.seqnum === 'number';
};

export const isMilestoneText = (obj: any): obj is MilestoneText => {
  return obj &&
    typeof obj.mvar === 'string' &&
    typeof obj.languagetype === 'string' &&
    typeof obj.htmltext === 'string';
};

export const isMilestoneAns = (obj: any): obj is MilestoneAns => {
  return obj &&
    typeof obj.ansid === 'string' &&
    typeof obj.mvar === 'string' &&
    ['yes', 'sometimes', 'not-yet'].includes(obj.value) &&
    typeof obj.seqnum === 'number';
};

export const isConcernAns = (obj: any): obj is ConcernAns => {
  return obj &&
    typeof obj.ansid === 'string' &&
    typeof obj.mvar === 'string' &&
    ['yes', 'no'].includes(obj.value) &&
    typeof obj.seqnum === 'number';
};

// Data validation utilities
export const validateMasterMilestone = (milestone: MasterMilestone): string[] => {
  const errors: string[] = [];
  
  if (!milestone.mvar?.trim()) errors.push('mvar is required');
  // Domain validation removed - domain association comes from AdminNode Order list, not milestone data
  if (milestone.itemage < 0 || milestone.itemage > 300) errors.push('itemage must be between 0-300 months');
  if (milestone.seqnum < 0) errors.push('seqnum must be non-negative');
  
  return errors;
};

export const validateMilestoneText = (text: MilestoneText): string[] => {
  const errors: string[] = [];
  
  if (!text.mvar?.trim()) errors.push('mvar is required');
  if (!text.languagetype?.trim()) errors.push('languagetype is required');
  if (!text.htmltext?.trim()) errors.push('htmltext is required');
  if (text.htmltext.length > 10000) errors.push('htmltext too long (max 10000 chars)');
  
  return errors;
};

export const validateMilestoneAns = (answer: MilestoneAns): string[] => {
  const errors: string[] = [];
  
  if (!answer.ansid?.trim()) errors.push('ansid is required');
  if (!answer.mvar?.trim()) errors.push('mvar is required');
  if (!['yes', 'sometimes', 'not-yet'].includes(answer.value)) {
    errors.push('value must be yes, sometimes, or not-yet');
  }
  if (answer.seqnum < 0) errors.push('seqnum must be non-negative');
  
  return errors;
};

export const validateConcernAns = (answer: ConcernAns): string[] => {
  const errors: string[] = [];
  
  if (!answer.ansid?.trim()) errors.push('ansid is required');
  if (!answer.mvar?.trim()) errors.push('mvar is required');
  if (!['yes', 'no'].includes(answer.value)) {
    errors.push('value must be yes or no');
  }
  if (answer.seqnum < 0) errors.push('seqnum must be non-negative');
  
  return errors;
};